/**
 * pages/dashboard/documents-list.tsx
 * Route: /dashboard/documents
 * Shows all documents from Supabase Storage + document_versions table.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";

const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

interface DocumentRow {
    document_id: string;
    document_type: string;
    file_name: string;
    storage_url: string;
    version_number: number;
    content_text: string;
    edited_by: string;
    comment: string;
    is_frozen: boolean;
    created_at: string;
}

const DocumentsList: React.FC = () => {
    const navigate = useNavigate();
    const [docs, setDocs] = useState<DocumentRow[]>(() => {
        try {
            const cached = sessionStorage.getItem("cached_my_documents");
            return cached ? JSON.parse(cached) : [];
        } catch {
            return [];
        }
    });
    const [loading, setLoading] = useState<boolean>(() => {
        try {
            return !sessionStorage.getItem("cached_my_documents");
        } catch {
            return true;
        }
    });
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem("auth.access_token") || "";
        fetch(`${API}/api/v1/my-documents?limit=50`, {
            headers: {
                "Authorization": `Bearer ${token}`
            }
        })
            .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
            .then(data => {
                const fetchedDocs = data.documents || [];
                setDocs(fetchedDocs);
                try {
                    sessionStorage.setItem("cached_my_documents", JSON.stringify(fetchedDocs));
                } catch (e) {
                    console.warn("Could not save docs to sessionStorage", e);
                }
            })
            .catch(err => {
                if (docs.length === 0) {
                    setError(String(err));
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const openEditor = (doc: DocumentRow) => {
        navigate(`/dashboard/collab/${doc.document_id}`, {
            state: {
                content: doc.content_text,
                documentType: doc.document_type || doc.comment,
                isNew: false,
            },
        });
    };

    const openHistory = (e: React.MouseEvent, docId: string) => {
        e.stopPropagation();
        navigate(`/dashboard/agents/document-history/${docId}`);
    };

    const handleDownload = async (e: React.MouseEvent, doc: DocumentRow) => {
        e.stopPropagation();
        const rawTitle = doc.document_type || doc.comment || "Documento_Legal";
        const safeTitle = rawTitle.replace(/[\\/:*?"<>|]/g, "_").trim().replace(/\s+/g, "_");
        const fileName = `${safeTitle}.docx`;

        setDownloadingId(doc.document_id);
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${doc.document_id}/download?title=${encodeURIComponent(rawTitle)}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error("Fallo al descargar documento desde backend");
            const blob = await res.blob();
            saveAs(blob, fileName);
        } catch (err) {
            console.warn("Backend proxy download failed, falling back to direct storage URL:", err);
            if (doc.storage_url) {
                const a = document.createElement("a");
                a.href = doc.storage_url;
                a.download = fileName;
                a.target = "_blank";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } finally {
            setDownloadingId(null);
        }
    };

    const handleRename = async (e: React.MouseEvent, doc: DocumentRow) => {
        e.stopPropagation();
        const currentTitle = doc.document_type || doc.comment || "Documento jurídico";
        const newName = window.prompt("Ingresa el nuevo nombre para este documento:", currentTitle);
        if (!newName || !newName.trim() || newName.trim() === currentTitle) {
            return;
        }

        const trimmedName = newName.trim();
        const previousDocs = [...docs];

        // ⚡ Optimistic Update: Update UI & cache IMMEDIATELY (0ms perceived delay)
        const updatedDocs = docs.map(d => 
            d.document_id === doc.document_id 
                ? { ...d, document_type: trimmedName, comment: trimmedName } 
                : d
        );
        setDocs(updatedDocs);
        try {
            sessionStorage.setItem("cached_my_documents", JSON.stringify(updatedDocs));
        } catch {}

        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${doc.document_id}/rename`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ name: trimmedName })
            });

            if (!res.ok) {
                // Revert on error
                setDocs(previousDocs);
                const data = await res.json().catch(() => ({}));
                alert(`Error: ${data.detail || "No se pudo cambiar el nombre del documento"}`);
            }
        } catch (err) {
            setDocs(previousDocs);
            console.error(err);
            alert("Error de conexión al intentar cambiar el nombre del documento");
        }
    };

    const handleDelete = async (e: React.MouseEvent, docId: string) => {
        e.stopPropagation();
        if (!window.confirm("¿Estás seguro de que deseas eliminar este documento? Esta acción es permanente.")) {
            return;
        }

        setDeletingId(docId);
        try {
            const res = await fetch(`${API}/api/v1/documents/${docId}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("auth.access_token") || ""}`
                }
            });
            if (res.ok) {
                setDocs(prev => {
                    const updated = prev.filter(d => d.document_id !== docId);
                    try {
                        sessionStorage.setItem("cached_my_documents", JSON.stringify(updated));
                    } catch {}
                    return updated;
                });
            } else {
                const data = await res.json();
                alert(`Error: ${data.detail || "No se pudo eliminar el documento"}`);
            }
        } catch (err) {
            console.error(err);
            alert("Error de conexión al intentar eliminar el documento");
        } finally {
            setDeletingId(null);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={s.root}>
            <div style={s.header}>
                <div style={s.headerTitle}>
                    <span style={s.icon}>📂</span>
                    <div>
                        <h1 style={s.title}>Mis Documentos</h1>
                        <p style={s.subtitle}>Todos los documentos jurídicos que has redactado</p>
                    </div>
                </div>
                <button style={s.btnNew} onClick={() => navigate("/dashboard/agents/document-drafting-v2")}>
                    ✍️ Nuevo documento
                </button>
            </div>

            {loading && <div style={s.centered}>Cargando documentos…</div>}

            {error && (
                <div style={s.errorBox}>
                    Error al cargar documentos: {error}
                </div>
            )}

            {!loading && !error && docs.length === 0 && (
                <div style={s.emptyState}>
                    <div style={s.emptyIcon}>📋</div>
                    <p style={s.emptyTitle}>No hay documentos aún</p>
                    <p style={s.emptyHint}>Genera tu primer documento jurídico y aparecerá aquí.</p>
                    <button style={s.btnNew} onClick={() => navigate("/dashboard/agents/document-drafting-v2")}>
                        ✍️ Crear documento
                    </button>
                </div>
            )}

            {!loading && !error && docs.length > 0 && (
                <div style={s.grid}>
                    {docs.map(doc => {
                        const title = doc.document_type || doc.comment || "Documento jurídico";
                        const date = doc.created_at
                            ? new Date(doc.created_at).toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "numeric" })
                            : "—";
                        const preview = doc.content_text
                            ? doc.content_text.replace(/<[^>]+>/g, " ").slice(0, 140).trim() + (doc.content_text.length > 140 ? "…" : "")
                            : "";

                        return (
                            <div key={doc.document_id} style={s.card} onClick={() => openEditor(doc)}>
                                <div style={s.cardTop}>
                                    <div style={s.docIcon}>⚖️</div>
                                    <div style={s.cardMeta}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                            <div style={s.cardTitle}>{title}</div>
                                            <button 
                                                style={s.btnRenameInline} 
                                                onClick={e => handleRename(e, doc)}
                                                disabled={renamingId === doc.document_id}
                                                title="Cambiar nombre del documento"
                                            >
                                                {renamingId === doc.document_id ? "⏳" : "✏️"}
                                            </button>
                                        </div>
                                        <div style={s.cardSub}>v{doc.version_number} · {date}</div>
                                    </div>
                                    {doc.is_frozen && <span style={s.frozenBadge}>🔒</span>}
                                </div>

                                {preview && <p style={s.preview}>{preview}</p>}

                                <div style={s.cardFooter}>
                                    <span style={s.editorText}>👤 {doc.edited_by || "Sistema"}</span>
                                    <div style={s.actions}>
                                        {doc.storage_url && (
                                            <button 
                                                type="button"
                                                style={s.btnDownload} 
                                                onClick={e => handleDownload(e, doc)}
                                                disabled={downloadingId === doc.document_id}
                                                title="Descargar con el nombre del documento"
                                            >
                                                {downloadingId === doc.document_id ? "⏳" : "↓ DOCX"}
                                            </button>
                                        )}
                                        <button style={s.btnAction} onClick={e => openHistory(e, doc.document_id)}>
                                            📋 Historial
                                        </button>
                                        <button style={s.btnPrimary} onClick={e => { e.stopPropagation(); openEditor(doc); }}>
                                            ✏️ Editar
                                        </button>
                                        <button 
                                            style={deletingId === doc.document_id ? s.btnDeleteLoading : s.btnDelete} 
                                            onClick={e => handleDelete(e, doc.document_id)} 
                                            disabled={deletingId === doc.document_id}
                                            title="Eliminar documento"
                                        >
                                            {deletingId === doc.document_id ? "⏳" : "🗑️"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const s: Record<string, React.CSSProperties> = {
    root: {
        minHeight: "100vh",
        background: "linear-gradient(135deg,#f8fafc 0%,#eef2ff 50%,#f1f5f9 100%)",
        fontFamily: "'Inter',system-ui,sans-serif",
        padding: "2rem",
    },
    header: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "2rem", flexWrap: "wrap", gap: "1rem",
    },
    headerTitle: { display: "flex", alignItems: "center", gap: "1rem" },
    icon: { fontSize: "2.5rem" },
    title: { fontSize: "1.75rem", fontWeight: 800, color: "#1e1b4b", margin: 0 },
    subtitle: { fontSize: "0.9rem", color: "#64748b", margin: "0.15rem 0 0" },
    btnNew: {
        padding: "0.6rem 1.25rem", borderRadius: "10px",
        background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
        color: "#fff", border: "none", fontWeight: 700, fontSize: "0.88rem",
        cursor: "pointer", boxShadow: "0 4px 12px rgba(99,102,241,.35)",
    },
    centered: { textAlign: "center", padding: "4rem", color: "#64748b", fontSize: "1rem" },
    errorBox: {
        margin: "1rem 0", padding: "1rem", background: "#fef2f2",
        border: "1px solid #fecaca", borderRadius: "10px", color: "#b91c1c",
    },
    emptyState: {
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "5rem 2rem", gap: "0.75rem",
    },
    emptyIcon: { fontSize: "3rem" },
    emptyTitle: { fontSize: "1.25rem", fontWeight: 700, color: "#1e293b", margin: 0 },
    emptyHint: { fontSize: "0.9rem", color: "#64748b", margin: 0, textAlign: "center" },
    grid: {
        display: "grid", gap: "1.25rem",
        gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))",
    },
    card: {
        background: "#fff", borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 8px rgba(0,0,0,.06)",
        padding: "1.25rem", cursor: "pointer",
        transition: "transform 0.15s,box-shadow 0.15s",
        display: "flex", flexDirection: "column", gap: "0.75rem",
    },
    cardTop: { display: "flex", alignItems: "flex-start", gap: "0.75rem" },
    docIcon: {
        width: "44px", height: "44px", borderRadius: "12px",
        background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.25rem", flexShrink: 0,
    },
    cardMeta: { flex: 1, minWidth: 0 },
    cardTitle: {
        fontSize: "0.95rem", fontWeight: 700, color: "#1e293b",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    },
    btnRenameInline: {
        background: "transparent", border: "none", cursor: "pointer",
        fontSize: "0.8rem", padding: "0 0.2rem", opacity: 0.7,
        transition: "opacity 0.2s", display: "inline-flex", alignItems: "center",
    },
    cardSub: { fontSize: "0.72rem", color: "#94a3b8", marginTop: "0.15rem" },
    frozenBadge: {
        fontSize: "0.85rem", flexShrink: 0,
    },
    preview: {
        fontSize: "0.82rem", color: "#475569", lineHeight: 1.6,
        margin: 0, overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
    } as React.CSSProperties,
    cardFooter: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderTop: "1px solid #f1f5f9", paddingTop: "0.75rem",
        flexWrap: "wrap", gap: "0.5rem",
    },
    editorText: { fontSize: "0.75rem", color: "#94a3b8" },
    actions: { display: "flex", gap: "0.5rem", alignItems: "center" },
    btnDownload: {
        padding: "0.3rem 0.6rem", borderRadius: "8px", fontSize: "0.72rem", fontWeight: 700,
        border: "1px solid #059669", background: "#ecfdf5", color: "#059669",
        cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center",
    },
    btnAction: {
        padding: "0.3rem 0.7rem", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
        border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", cursor: "pointer",
    },
    btnPrimary: {
        padding: "0.3rem 0.7rem", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 700,
        background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
        color: "#fff", border: "none", cursor: "pointer",
    },
    btnDelete: {
        padding: "0.3rem 0.5rem", borderRadius: "8px", fontSize: "0.85rem",
        background: "#fff1f2", color: "#e11d48", border: "1px solid #fecdd3",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.2s, opacity 0.2s",
    },
    btnDeleteLoading: {
        padding: "0.3rem 0.5rem", borderRadius: "8px", fontSize: "0.85rem",
        background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
        cursor: "not-allowed", display: "flex", alignItems: "center", justifyContent: "center",
        opacity: 0.7,
    },
};

export default DocumentsList;
